import { cloneDeepWith } from 'lodash-es';
import wildcardMatch from 'wildcard-match';

// Move these to dedicated file
export const deepCopyObject = <T>(object: T): T => JSON.parse(JSON.stringify(object));

/**
 * Moves an item inside an array, from one index to another.
 *
 * @param arr array of unknown elements
 * @param old_index previus index of the element that will be moved
 * @param new_index newe index of the element that will be moved
 * @returns new array with moved element
 */
export const arrayMove = (arr: unknown[], old_index: number, new_index: number) => {
  const newArray = [...arr];

  if (new_index >= newArray.length) {
    let k = new_index - newArray.length + 1;
    while (k--) {
      newArray.push(undefined);
    }
  }
  newArray.splice(new_index, 0, newArray.splice(old_index, 1)[0]);
  return newArray;
};

export type SortValue = string | number | null;

export const sortByValue = <T>(
  items: T[],
  getValue: (item: T) => SortValue,
  direction: 'ascending' | 'descending',
): T[] => {
  const factor = direction === 'descending' ? -1 : 1;

  return [...items].sort((a, b) => {
    const left = getValue(a);
    const right = getValue(b);

    if (left == null && right == null) return 0;
    if (left == null) return 1;
    if (right == null) return -1;

    if (typeof left === 'number' && typeof right === 'number') return (left - right) * factor;

    return String(left).localeCompare(String(right)) * factor;
  });
};

export const splitArrayIntoTwoEqualParts = (items?: unknown[]) => {
  let evenArray: unknown[] = [];
  let oddArray: unknown[] = [];

  if (!items) return [evenArray, oddArray];

  if (items.length > 10) {
    evenArray = items.slice(0, Math.ceil(items.length / 2));
    oddArray = items.slice(Math.ceil(items.length / 2), items.length);
  } else {
    evenArray = items;
  }

  return [evenArray, oddArray];
};

export function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const matchesMultiple = (config: string[] | readonly string[], test: string): boolean => {
  return config.some((pattern) => wildcardMatch(pattern)(test));
};

export function matchStrings(first: RegExp | string, second: RegExp | string): boolean {
  if (typeof first === 'string' && typeof second === 'string') {
    return first === second || second.includes(first) || first.includes(second);
  }

  if (typeof first !== 'string' && typeof second === 'string') {
    return first.test(second);
  }

  if (typeof first === 'string' && typeof second !== 'string') {
    return (second as RegExp).test(first);
  }

  if (typeof first !== 'string' && typeof second !== 'string') {
    return first.test(String(second)) || second.test(String(first)) || String(first) === String(second);
  }

  return false;
}

export function omitDeep<T, K>(collection: T, excludeKeys: string[]): K {
  function omitFn(value: any) {
    if (value && typeof value === 'object') {
      excludeKeys.forEach((key) => {
        delete value[key];
      });
    }
  }

  return cloneDeepWith(collection, omitFn);
}
